import { getLocaleCode } from '@/constants/languages';
import { resolvedLanguage, t } from '@/i18n';

export const arrayToStringWithComas = (array?: string[]) => {
    if (!array || array.length < 1) return '';
    if (array.length === 1) return array[0] ?? '';

    const lastElement = array.splice(array.length - 1, array.length);
    return `${array.join(`${t('separator')} `)} ${t('finalSeparator')} ${lastElement[0]}`;
};

export const hashString = (str: string): number => {
    if (str.length === 0) return 0;

    let hash = 0;
    let i;
    let chr;
    let len;

    for (i = 0, len = str.length; i < len; i++) {
        chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
};

export function slugify(str: string): string {
    return str
        ? str
              .toLowerCase()
              .replace(/[ñ]/g, 'n')
              .match(/[A-Z]{2,}(?=[A-Z][a-z]+\d*|\b)|[A-Z]?[a-z]+\d*|[A-Z]|\d+/g)!
              .join('-')
        : '';
}

export const normalize = (string: string): string => string.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const parseToScreamingSnakeCase = (string: string): string =>
    string.trim().replace(/[\s]+/g, '_').replace(/[\W]+/g, '').toUpperCase();

export const clearStringToOnlyDigitsAndSeparators = (value: string): string => {
    const regex = /[^\d.,]/gi;
    return value.replace(regex, '');
};

// JSON

export const jsonParse = <T>(str?: string): T | undefined => {
    if (!str) return undefined;

    try {
        return JSON.parse(str);
    } catch {
        return undefined;
    }
};

// MONEY

const DEFAULT_CURRENCY = 'EUR';

const CURRENCY_LOCALE: Map<string, string> = new Map([
    ['EUR', 'de-DE'],
    ['GBP', 'en-GB'],
    ['USD', 'en-US'],
    ['RUB', 'ru-RU'],
]);

export const convertNumberToMoney = (value: number | string, currency?: string): string => {
    const number = typeof value === 'string' ? Number(clearStringToOnlyDigitsAndSeparators(value)) : value;
    return number.toLocaleString(getLocaleCode(resolvedLanguage!, true), {
        style: 'currency',
        currency: currency ?? DEFAULT_CURRENCY,
        minimumFractionDigits: 2,
    });
};

export const moneyString = (value: number | string, currency?: string): string => {
    const currencyCode = currency ?? DEFAULT_CURRENCY;
    const currencyLocale = CURRENCY_LOCALE.get(currencyCode) ?? CURRENCY_LOCALE.get(DEFAULT_CURRENCY);
    const formatter = new Intl.NumberFormat(currencyLocale, {
        style: 'currency',
        currency: currencyCode,
    });

    return (
        formatter
            .format(0)
            // eslint-disable-next-line no-irregular-whitespace
            .replace(/ /g, '')
            .replace(/[?!^\d.,]/gi, '-')
            .replace(/-+/g, '-')
            .replace(/-/g, `${value}`)
    );
};

export const getFullName = (person: { firstName: string; lastName: string }): string =>
    `${person.firstName} ${person.lastName}`;

export const getInitialsName = (person: { firstName: string; lastName: string }): string =>
    `${person.firstName[0].toUpperCase()} ${person.lastName[0].toUpperCase()}`;
